import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { useHistory } from 'react-router';
import {
  AppBar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from '../../Contexts/ThemeContext';
import AOS from 'aos';

import LaptopSkeletonIllustration from '../../Assets/svg/LaptopSkeleton';
import DashboardScreenshot from '../../Assets/img/dashboard.png';
import DashboardScreenshotWebP from '../../Assets/img/dashboard.webp';
import ColorLogo from '../../Assets/svg/Logo_square-notext-02.svg';
import WhiteLogo from '../../Assets/svg/Logo_square-notext-white-02.svg';

import Contact from './contact';
import { Copyright } from '../../Helpers/utils';
import content from './content';

export const useDarkMode = () => {
  const [themeMode, setTheme] = useState('light');
  const [mountedComponent, setMountedComponent] = useState(false);

  const setMode = (mode) => {
    try {
      window.localStorage.setItem('themeMode', mode);
    } catch {
      /* do nothing */
    }
    setTheme(mode);
  };

  const themeToggler = () => {
    themeMode === 'light' ? setMode('dark') : setMode('light');
  };

  useEffect(() => {
    try {
      const localTheme = window.localStorage.getItem('themeMode');
      localTheme ? setTheme(localTheme) : setMode('light');
    } catch {
      setMode('light');
    }
    setMountedComponent(true);
  }, []);

  return [themeMode, themeToggler, mountedComponent];
};

// ─── Reusable section CTA button ────────────────────────────────────────────
const DemoButton = ({ onClick, variant = 'contained', sx = {} }) => (
  <Button
    variant={variant}
    size="large"
    onClick={onClick}
    sx={{
      px: 4,
      py: 1.5,
      fontWeight: 700,
      fontSize: '1rem',
      borderRadius: 2,
      ...(variant === 'outlined' && {
        borderColor: 'white',
        color: 'white',
        '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
      }),
      ...sx,
    }}
  >
    {content.ctaPrimary}
  </Button>
);

export default function LandingPage() {
  const history = useHistory();

  const scrollToContact = () => {
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  React.useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
    AOS.init({
      once: true,
      delay: 0,
      duration: 800,
      offset: 0,
      easing: 'ease-in-out',
    });
  }, []);

  const [themeMode, themeToggler, mountedComponent] = useDarkMode();

  useEffect(() => {
    AOS.refresh();
  }, [mountedComponent, themeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* ─── Nav ──────────────────────────────────────────────────────────── */}
      <AppBar position="absolute">
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', pr: '24px', pl: '24px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={WhiteLogo}
              alt="BridgeFirst logo"
              sx={{ height: 36, width: 'auto' }}
            />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, display: { xs: 'none', sm: 'block' } }}>
              BridgeFirst
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={scrollToContact}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.6)',
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            >
              Request a Demo
            </Button>
            <IconButton
              aria-label="login"
              color="inherit"
              onClick={() => history.push('/login')}
              id="account-icon"
              size="large"
            >
              <AccountCircle />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Paper elevation={0} sx={{ paddingTop: '64px' }}>

        {/* ─── Hero ─────────────────────────────────────────────────────────── */}
        <Box
          position="relative"
          sx={{
            backgroundColor: theme.palette.primary.main,
            marginBottom: '120px',
            pb: '140px', // extra bottom padding keeps content well above the wave
          }}
        >
          <Container sx={{ pt: { xs: 6, md: 8 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6} data-aos="fade-right">
                <Chip
                  label="Financial Planning, Reimagined"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontWeight: 600,
                    mb: 2,
                    letterSpacing: 0.5,
                  }}
                />
                <Typography
                  component="h1"
                  variant="h3"
                  sx={{ fontWeight: 700, color: 'common.white', mb: 2, lineHeight: 1.2 }}
                >
                  {content.title}
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{ color: 'rgba(255,255,255,0.85)', mb: 4, lineHeight: 1.6 }}
                >
                  {content.subtitle}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <DemoButton onClick={scrollToContact} />
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderRadius: 2,
                      borderColor: 'rgba(255,255,255,0.6)',
                      color: 'white',
                      '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    {content.ctaSecondary}
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} data-aos="fade-left">
                <Box sx={{ position: 'relative', mx: 'auto' }}>
                  <Box position="relative" zIndex={2} maxWidth={1} height="auto" sx={{ verticalAlign: 'middle' }}>
                    <LaptopSkeletonIllustration />
                  </Box>
                  <Box
                    position="absolute"
                    top="8.4%"
                    left="12%"
                    width="76%"
                    height="83%"
                    border={`1px solid ${theme.palette.alternate.dark}`}
                    zIndex={3}
                  >
                    <Box
                      component="picture"
                      sx={{ display: 'block', width: '100%', height: '100%' }}
                    >
                      <source srcSet={DashboardScreenshotWebP} type="image/webp" />
                      <Box
                        component="img"
                        src={DashboardScreenshot}
                        alt="BridgeFirst application dashboard showing financial planning interface"
                        width={1}
                        height={1}
                        loading="eager"
                        sx={{ objectFit: 'cover', width: '100%', height: '100%' }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
          {/* Wave divider — tall enough that the cut starts below the button row */}
          <Box
            component="svg"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1921 273"
            sx={{ position: 'absolute', width: '100%', left: 0, bottom: 0, right: 0, zIndex: 1, height: '22%' }}
          >
            <polygon fill={theme.palette.background.paper} points="0,273 1921,273 1921,0" />
          </Box>
        </Box>

        {/* ─── Problem Section ──────────────────────────────────────────────── */}
        <Container sx={{ mb: '120px' }} data-aos="fade-up">
          <Box textAlign="center" mb={6}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {content.problemSection.title}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {content.problemSection.subtitle}
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {content.problemSection.problems.map((problem, i) => (
              <Grid key={i} item xs={12} md={4}>
                <Card
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderTop: `4px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    {problem.heading}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {problem.body}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* ─── How It Works ─────────────────────────────────────────────────── */}
        <Box
          id="how-it-works"
          sx={{ backgroundColor: '#f5f7fa', py: '80px', mb: 0 }}
          data-aos="fade-up"
        >
          <Container>
            <Box textAlign="center" mb={6}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {content.howItWorks.title}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 640, mx: 'auto' }}>
                {content.howItWorks.subtitle}
              </Typography>
            </Box>
            <Grid container spacing={4}>
              {content.howItWorks.steps.map((step, i) => (
                <Grid key={i} item xs={12} md={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        mb: 2,
                        flexShrink: 0,
                      }}
                    >
                      {step.number}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {step.body}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box textAlign="center" mt={6}>
              <DemoButton onClick={scrollToContact} />
            </Box>
          </Container>
        </Box>

        {/* ─── Speed / Stats ────────────────────────────────────────────────── */}
        <Box
          sx={{ backgroundColor: theme.palette.primary.main, py: '80px', mb: '120px' }}
          data-aos="fade-up"
        >
          <Container>
            <Box textAlign="center" mb={6}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                {content.speedSection.title}
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 600, mx: 'auto' }}>
                {content.speedSection.subtitle}
              </Typography>
            </Box>
            <Grid container spacing={4} justifyContent="center">
              {content.speedSection.stats.map((stat, i) => (
                <Grid key={i} item xs={6} md={3}>
                  <Box textAlign="center">
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ─── What Is BridgeFirst ──────────────────────────────────────────── */}
        <Container sx={{ mb: '120px' }} data-aos="fade-up">
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              mb: 6,
            }}
          >
            <Box width="200px" zIndex={3} mb={2}>
              <Box
                component="img"
                src={ColorLogo}
                alt="BridgeFirst logo"
                width={1}
                height={1}
                sx={{ objectFit: 'contain' }}
              />
            </Box>
            <Box width="100%" textAlign="center" mb={2}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {content.whatIs.title}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 760, mx: 'auto' }}>
                {content.whatIs.subtitle}
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={4}>
            {content.whatIs.bulletPoints.map((item, i) => (
              <Grid key={i} item xs={12} md={3}>
                <ListItem
                  component="div"
                  disableGutters
                  sx={{ flexDirection: 'column', alignItems: 'center', padding: 0 }}
                >
                  <Box component={ListItemAvatar} marginBottom={1} minWidth="auto !important">
                    <Box color={theme.palette.primary.main}>{item.icon}</Box>
                  </Box>
                  <ListItemText
                    primary={item.title}
                    secondary={item.subtitle}
                    primaryTypographyProps={{ variant: 'h6', gutterBottom: true, align: 'center' }}
                    secondaryTypographyProps={{ align: 'center' }}
                    sx={{ '& .MuiListItemText-primary': { fontWeight: 700 }, margin: 0 }}
                  />
                </ListItem>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* ─── Feature Grid ─────────────────────────────────────────────────── */}
        <Box sx={{ backgroundColor: '#f5f7fa', py: '80px', mb: '120px' }} data-aos="fade-up">
          <Container>
            <Box textAlign="center" mb={6}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {content.features.title}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {content.features.subtitle}
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {content.features.items.map((feature, i) => (
                <Grid key={i} item xs={12} sm={6} md={3}>
                  <Card
                    elevation={1}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 2,
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: 4 },
                    }}
                  >
                    <Box color={theme.palette.primary.main} mb={1.5}>
                      {feature.icon}
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {feature.body}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ─── Comparison Table ─────────────────────────────────────────────── */}
        <Container sx={{ mb: '120px' }} data-aos="fade-up">
          <Box textAlign="center" mb={6}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {content.comparison.title}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {content.comparison.subtitle}
            </Typography>
          </Box>
          <TableContainer component={Card} elevation={2} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                  {content.comparison.headers.map((header, i) => (
                    <TableCell
                      key={i}
                      align={i === 0 ? 'left' : 'center'}
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: i === 1 ? '1rem' : '0.875rem',
                        py: 2,
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {content.comparison.rows.map((row, i) => (
                  <TableRow
                    key={i}
                    sx={{
                      backgroundColor: i % 2 === 0 ? 'white' : '#f9fafb',
                      '&:hover': { backgroundColor: '#eef2f7' },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500, py: 1.5 }}>{row.feature}</TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      {row.bf
                        ? <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                        : <CancelIcon sx={{ color: '#c62828' }} />}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      {row.excel
                        ? <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                        : <CancelIcon sx={{ color: '#c62828' }} />}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      {row.enterprise
                        ? <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                        : <CancelIcon sx={{ color: '#c62828' }} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box textAlign="center" mt={5}>
            <DemoButton onClick={scrollToContact} />
          </Box>
        </Container>

        {/* ─── Who Is BridgeFirst For ───────────────────────────────────────── */}
        <Box
          sx={{ backgroundColor: theme.palette.primary.main, py: '80px', mb: '120px' }}
          data-aos="fade-up"
        >
          <Container>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
              {/* Colored logo on a white circle badge */}
              <Box
                sx={{
                  width: 200,
                  height: 100,
                  borderRadius: '16px',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  flexShrink: 0,
                  px: 2,
                }}
              >
                <Box
                  component="img"
                  src={ColorLogo}
                  alt="BridgeFirst logo"
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Box>
              <Box width="100%" textAlign="center" mb={6}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                  {content.whoIs.title}
                </Typography>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 700, mx: 'auto' }}>
                  {content.whoIs.subtitle}
                </Typography>
              </Box>
              <Grid container spacing={4}>
                {content.whoIs.bulletPoints.map((item, i) => (
                  <Grid key={i} item xs={12} md={3}>
                    <ListItem
                      component="div"
                      disableGutters
                      sx={{ flexDirection: 'column', alignItems: 'center', padding: 0 }}
                    >
                      <Box component={ListItemAvatar} marginBottom={1} minWidth="auto !important">
                        <Box sx={{ color: 'rgba(255,255,255,0.9)' }}>{item.icon}</Box>
                      </Box>
                      <ListItemText
                        primary={item.title}
                        secondary={item.subtitle}
                        primaryTypographyProps={{ variant: 'h6', gutterBottom: true, align: 'center' }}
                        secondaryTypographyProps={{ align: 'center' }}
                        sx={{
                          '& .MuiListItemText-primary': { fontWeight: 700, color: 'white' },
                          '& .MuiListItemText-secondary': { color: 'rgba(255,255,255,0.75)' },
                          margin: 0,
                        }}
                      />
                    </ListItem>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>
        </Box>


        {/* ─── Contact ──────────────────────────────────────────────────────── */}
        <Box id="contact-section">
          <Contact />
        </Box>

        <Copyright sx={{ mt: 8, mb: 4 }} />
      </Paper>
    </ThemeProvider>
  );
}
